import { useState } from 'react'
import type { ReactNode } from 'react'

/**
 * One labelled input with the room its error occupies — the house form control,
 * promoted out of the broker feature when the carrier forms became its second home.
 *
 * The error lives next to the field it belongs to rather than in a heap at the bottom
 * of the form: on a thirteen-field screen, "the state code is wrong" is only useful
 * if you can see which box it is talking about. Usually a sentence; a node when the
 * sentence needs a way somewhere — "already registered to X. Open X." (TRKC-83).
 *
 * Label, input and message are siblings tied together by id — deliberately not an
 * input wrapped in a label. Wrapping would fold the error text into the field's
 * accessible name, so a screen reader would announce the box as "Name that doesn't
 * look like a business name" instead of naming it and then describing what is wrong.
 *
 * A field may carry a `display` presentation (money, today): it applies only at
 * rest. While focused the raw value is what is shown and edited, so the caret works
 * on "75000", never inside "$75,000.00" — and nothing downstream ever has to parse a
 * formatted string back.
 */
export function FormField({
  name,
  label,
  value,
  onChange,
  error,
  type = 'text',
  maxLength,
  required = false,
  placeholder,
  width = 'normal',
  inputMode,
  display,
  multiline = false,
  rows = 2,
}: {
  name: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: ReactNode
  type?: string
  maxLength?: number
  required?: boolean
  placeholder?: string
  width?: 'normal' | 'wide' | 'narrow'
  inputMode?: 'decimal' | 'numeric'
  display?: (value: string) => string
  /** A short textarea instead of an input — for notes-shaped fields. */
  multiline?: boolean
  /** How tall that textarea starts. A box meant for a hundred pasted lines says so. */
  rows?: number
}) {
  const errorId = `${name}-error`
  const className = width === 'normal' ? 'field' : `field field--${width}`

  const [isFocused, setIsFocused] = useState(false)
  const shown = display !== undefined && !isFocused ? display(value) : value

  if (multiline) {
    return (
      <div className={className}>
        <label htmlFor={name}>{label}</label>
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          maxLength={maxLength}
          placeholder={placeholder}
          rows={rows}
          aria-invalid={error !== undefined}
          aria-describedby={error === undefined ? undefined : errorId}
        />
        {error !== undefined && (
          <span className="field__error" id={errorId} role="alert">
            {error}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className={className}>
      <label htmlFor={name}>{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        inputMode={inputMode}
        value={shown}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        required={required}
        maxLength={maxLength}
        placeholder={placeholder}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : errorId}
      />
      {error !== undefined && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

/**
 * A yes/no fact beside its sentence — an agent who asked for no emails, a flatbed that
 * carries chains. The box sits inside its label on purpose, unlike a text field: there
 * is no error text to keep out of the accessible name, and the whole sentence becomes
 * the click target.
 */
export function FormCheckbox({
  name,
  label,
  checked,
  onChange,
  disabled,
}: {
  name: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  /** Listed but not choosable — the broadcast's agents it cannot reach, explained beside the box. */
  disabled?: boolean
}) {
  return (
    <div className="field field--check">
      <label htmlFor={name}>
        <input
          id={name}
          name={name}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        {label}
      </label>
    </div>
  )
}

/**
 * A labelled select with the same contract — used where the domain accepts one value
 * from a closed set (equipment types), so a typo is not even possible. An option can be
 * listed but not chosen: the posting form shows a truck it cannot offer, with the
 * reason in its label, so "why can't I post my truck?" answers itself (Stage 2, 2.1).
 */
export function FormSelect({
  name,
  label,
  value,
  onChange,
  options,
  error,
  required = false,
  width = 'normal',
  disabled = false,
}: {
  name: string
  label: string
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>
  error?: ReactNode
  required?: boolean
  width?: 'normal' | 'wide' | 'narrow'
  /** Nothing to choose yet — the list this select belongs to is still on its way. */
  disabled?: boolean
}) {
  const errorId = `${name}-error`
  const className = width === 'normal' ? 'field' : `field field--${width}`

  return (
    <div className={className}>
      <label htmlFor={name}>{label}</label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        disabled={disabled}
        aria-invalid={error !== undefined}
        aria-describedby={error === undefined ? undefined : errorId}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {error !== undefined && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}
